<template>
  <div class="excel-page">
    <el-container>
      <el-header class="header">
        <h1>🔑 卡密格式化工具</h1>
      </el-header>
      <el-row :gutter="24">
        <el-col :span="22" :offset="1">
          <div class="form-container">
            <el-form label-width="120px">
              <el-form-item label="输入卡密">
                <el-input
                  v-model="cardCodeInput"
                  type="textarea"
                  :rows="8"
                  placeholder="请输入卡密，用逗号分割。例如：8979874654,878456456456465,79874564654564,54321231321"
                  class="card-code-textarea"
                />
              </el-form-item>
              <el-form-item>
                <el-button
                  type="primary"
                  @click="exportToExcel"
                  class="export-btn"
                  :loading="exporting"
                >
                  📊 格式化并导出Excel
                </el-button>
                <el-button
                  @click="clearInput"
                  class="clear-btn"
                >
                  🗑️ 清空输入
                </el-button>
              </el-form-item>
            </el-form>
            
            <div class="preview-section" v-if="formattedCodes.length > 0">
              <div class="preview-header">
                <span>📋 预览结果</span>
                <span class="count-info">共 {{ formattedCodes.length }} 个卡密</span>
              </div>
              <el-table
                :data="previewData"
                border
                stripe
                class="preview-table"
              >
                <el-table-column type="index" label="序号" width="80" />
                <el-table-column prop="cardCode" label="卡密" />
                <!-- <el-table-column prop="status" label="状态" width="120">
                  <template #default="scope">
                    <el-tag type="success" size="small">有效</el-tag>
                  </template>
                </el-table-column> -->
              </el-table>
            </div>
          </div>
        </el-col>
      </el-row>
    </el-container>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { ElMessage } from 'element-plus';
import * as XLSX from 'xlsx';

const cardCodeInput = ref('');
const exporting = ref(false);

const formattedCodes = computed(() => {
  if (!cardCodeInput.value.trim()) {
    return [];
  }
  return cardCodeInput.value
    .split(',')
    .map(code => code.trim())
    .filter(code => code.length > 0);
});

const previewData = computed(() => {
  return formattedCodes.value.map((code, index) => ({
    index: index + 1,
    cardCode: code,
    status: '有效'
  }));
});

const exportToExcel = () => {
  if (formattedCodes.value.length === 0) {
    ElMessage.warning('请输入卡密');
    return;
  }

  exporting.value = true;
  
  try {
    const exportData = formattedCodes.value.map((code, index) => ({
      序号: index + 1,
      卡密: code
    }));
    
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, '卡密列表');
    
    XLSX.writeFile(workbook, '卡密列表.xlsx');
    
    ElMessage.success('导出成功！');
  } catch (error) {
    console.error('导出失败:', error);
    ElMessage.error('导出失败，请重试');
  } finally {
    exporting.value = false;
  }
};

const clearInput = () => {
  cardCodeInput.value = '';
  ElMessage.success('已清空输入');
};
</script>

<style scoped>
.excel-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8f5 100%);
}

.header {
  background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
  color: white;
  text-align: center;
  line-height: 70px;
  box-shadow: 0 4px 20px rgba(24, 144, 255, 0.3);
}

.header h1 {
  margin: 0;
  font-size: 28px;
  font-weight: 600;
  letter-spacing: 2px;
  text-shadow: 0 2px 8px rgba(0,0,0,0.2);
}

.form-container {
  margin-top: 40px;
  padding: 40px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(24, 144, 255, 0.1);
}

.card-code-textarea {
  font-family: 'Courier New', monospace;
  font-size: 14px;
  line-height: 1.6;
}

:deep(.el-textarea__inner) {
  padding: 12px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: box-shadow 0.3s ease;
}

:deep(.el-textarea__inner:hover) {
  box-shadow: 0 4px 12px rgba(24, 144, 255, 0.2);
}

:deep(.el-textarea__inner:focus) {
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

.export-btn {
  padding: 12px 32px;
  font-size: 15px;
  border-radius: 8px;
  transition: background-color 0.3s ease, box-shadow 0.3s ease;
  font-weight: 500;
  background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
  border: none;
}

.export-btn:hover {
  background: linear-gradient(135deg, #096dd9 0%, #1890ff 100%);
  box-shadow: 0 6px 16px rgba(24, 144, 255, 0.4);
}

.clear-btn {
  padding: 12px 24px;
  font-size: 14px;
  border-radius: 8px;
  transition: all 0.3s ease;
  font-weight: 500;
}

.clear-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

.preview-section {
  margin-top: 32px;
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 2px solid #1890ff;
}

.preview-header span:first-child {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

.count-info {
  font-size: 14px;
  color: #606266;
  background: #f5f7fa;
  padding: 6px 16px;
  border-radius: 20px;
}

.preview-table {
  margin-top: 16px;
}

:deep(.el-table) {
  border-radius: 8px;
  overflow: hidden;
}

:deep(.el-table__header-wrapper .el-table__cell) {
  color: white !important;
  background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%) !important;
}

:deep(.el-table__header th) {
  background: transparent;
  color: white;
  font-weight: 600;
  font-size: 14px;
}

:deep(.el-table__body tr:hover > td) {
  background-color: #f5f7fa !important;
}

:deep(.el-table__body td) {
  padding: 12px 0;
  font-size: 14px;
  color: #303133;
}

:deep(.el-form-item__label) {
  font-weight: 500;
  color: #303133;
  font-size: 15px;
}

:deep(.el-button--primary) {
  border: none;
  font-weight: 500;
  transition: background-color 0.3s ease, box-shadow 0.3s ease;
}

:deep(.el-button--primary:hover) {
  background: linear-gradient(135deg, #096dd9 0%, #1890ff 100%);
  box-shadow: 0 6px 16px rgba(24, 144, 255, 0.4);
}
</style>